/* globals document, process */

import React, { Component, PropTypes } from 'react';
import GoogleMap from 'google-map-react';
import supercluster from 'supercluster';
import { intlShape } from 'react-intl';
import { autobind } from 'core-decorators';

import { latLngShape } from 'utils/propTypes';

import ClusterMapMarker from 'AppComponents/MapMarker/Cluster';
import CurrentLocation from 'AppComponents/MapMarker/CurrentLocation';

import ChargerMarker from './components/ChargerMarker';


class ChargerMap extends Component {

  static propTypes = {
    center: latLngShape.isRequired,
    chargers: PropTypes.arrayOf(PropTypes.object).isRequired,
    currentLocation: latLngShape,
    hoveredCharger: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onBoundsChange: PropTypes.func.isRequired,
    onChargerMouseEnter: PropTypes.func.isRequired,
    onChargerMouseLeave: PropTypes.func.isRequired,
    onClick: PropTypes.func.isRequired,
    onClusterClick: PropTypes.func.isRequired,
    onMapLoaded: PropTypes.func.isRequired,
    onMarkerClick: PropTypes.func.isRequired,
    selectedCharger: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    zoom: PropTypes.number,
  };

  static defaultProps = {
    currentLocation: null,
    hoveredCharger: null,
    selectedCharger: null,
    zoom: null,
  };

  static contextTypes = {
    router: PropTypes.any.isRequired,
    intl: intlShape.isRequired,
    isCarBrowser: PropTypes.bool.isRequired,
  };

  constructor(props, context) {
    super(props, context);
    this.clusters = [];
    this.state = {
      bounds: [-180, 2, -27, 72],
      mapKey: 1,
    };
  }

  @autobind
  onMapLoaded({ map, maps }) {
    this.map = map;
    this.maps = maps;
    if (this.context.isCarBrowser) this.replaceDefaultUI();
    this.props.onMapLoaded({ map, maps });
  }

  replaceDefaultUI() {
    const zoomControlDiv = document.createElement('div');
    zoomControlDiv.className = 'tesla-controls';
    zoomControlDiv.index = 1;

    const controlWrapper = document.createElement('div');
    controlWrapper.className = 'tesla-controls-wrapper';
    zoomControlDiv.appendChild(controlWrapper);

    const zoomInButton = document.createElement('div');
    zoomInButton.className = 'btn btn-default zoom-in';
    zoomInButton.title = 'Zoom in';
    controlWrapper.appendChild(zoomInButton);

    const zoomOutButton = document.createElement('div');
    zoomOutButton.className = 'btn btn-default zoom-out';
    zoomOutButton.title = 'Zoom out';
    controlWrapper.appendChild(zoomOutButton);

    this.maps.event.addDomListener(zoomInButton, 'click', () => {
      this.map.setZoom(this.map.getZoom() + 1);
    });

    this.maps.event.addDomListener(zoomOutButton, 'click', () => {
      this.map.setZoom(this.map.getZoom() - 1);
    });

    const panControlDiv = document.createElement('div');
    panControlDiv.className = 'tesla-pan';
    panControlDiv.index = 1;

    const panLeft = document.createElement('div');
    panLeft.className = 'btn btn-default pan-left';
    panLeft.title = 'Pan left';
    panControlDiv.appendChild(panLeft);

    this.maps.event.addDomListener(panLeft, 'click', this.handlePanClick.bind(this, 'left'));

    const upDownWrapper = document.createElement('div');
    upDownWrapper.className = 'up-down-wrapper';
    panControlDiv.appendChild(upDownWrapper);

    const panUp = document.createElement('div');
    panUp.className = 'btn btn-default pan-up';
    panUp.title = 'Pan up';
    upDownWrapper.appendChild(panUp);

    this.maps.event.addDomListener(panUp, 'click', this.handlePanClick.bind(this, 'up'));

    const panDown = document.createElement('div');
    panDown.className = 'btn btn-default pan-down';
    panDown.title = 'Pan down';
    upDownWrapper.appendChild(panDown);

    this.maps.event.addDomListener(panDown, 'click', this.handlePanClick.bind(this, 'down'));

    const panRight = document.createElement('div');
    panRight.className = 'btn btn-default pan-right';
    panRight.title = 'Pan right';
    panControlDiv.appendChild(panRight);

    this.maps.event.addDomListener(panRight, 'click', this.handlePanClick.bind(this, 'right'));

    this.map.controls[this.maps.ControlPosition.TOP_RIGHT].push(zoomControlDiv);
    this.map.controls[this.maps.ControlPosition.RIGHT_BOTTOM].push(panControlDiv);
    this.map.setOptions({ disableDefaultUI: true });
  }

  handlePanClick(direction) {
    const bounds = this.map.getBounds();
    const span = bounds.toSpan();
    const center = bounds.getCenter();

    switch (direction) {
      case 'left':
        this.map.setCenter({ lat: center.lat(), lng: center.lng() - (span.lng() / 3) });
        break;

      case 'right':
        this.map.setCenter({ lat: center.lat(), lng: center.lng() + (span.lng() / 3) });
        break;

      case 'up':
        this.map.setCenter({ lat: center.lat() + (span.lat() / 2), lng: center.lng() });
        break;

      case 'down':
        this.map.setCenter({ lat: center.lat() - (span.lat() / 2), lng: center.lng() });
        break;

      default:
        break;
    }
  }

  handleClick(key) {
    const cluster = this.clusters[key];
    if (cluster.properties !== undefined) {
      this.props.onClusterClick(key, cluster);
    } else if (cluster.charger !== undefined) {
      this.props.onMarkerClick(key, cluster);
    }
  }

  @autobind
  handleMapChange({ center, zoom, bounds, marginBounds }) {
    this.props.onBoundsChange({ center, zoom, bounds, marginBounds });
    this.setState({
      bounds: [
        bounds.nw.lng,
        bounds.se.lat,
        bounds.se.lng,
        bounds.nw.lat,
      ],
    });
  }

  updateClusters() {
    const points = this.props.chargers.map(charger => ({
      type: 'Feature',
      charger,
      geometry: charger.custom_coords || charger.address.coords,
    }));
    if (points.length > 0) {
      const cluster = supercluster({
        radius: 30,
        maxZoom: 11,
        extent: 256,
      });
      cluster.load(points);
      this.clusters = cluster.getClusters(this.state.bounds, this.props.zoom);
      return this.clusters;
    }
    this.clusters = [];
    return this.clusters;
  }

  reRenderMap() {
    const { mapKey } = this.state;
    this.setState({ mapKey: mapKey + 1 });
  }

  render() {
    this.updateClusters();
    const { center, zoom, currentLocation } = this.props;
    const router = this.context.router;
    const clusters = this.clusters.map((cluster, i) => {
      const boundClick = () => this.handleClick(i);
      return (
        cluster.charger === undefined ?
          <ClusterMapMarker
            lat={cluster.geometry.coordinates[1]}
            lng={cluster.geometry.coordinates[0]}
            points={cluster.properties.point_count_abbreviated}
            onClick={boundClick}
            key={i} // eslint-disable-line react/no-array-index-key
          />
        :
          <ChargerMarker
            charger={cluster.charger}
            lat={cluster.geometry.coordinates[1]}
            lng={cluster.geometry.coordinates[0]}
            selected={this.props.selectedCharger === cluster.charger.pk}
            hovered={this.props.hoveredCharger === cluster.charger.pk}
            key={cluster.charger.pk}
            onClick={boundClick}
            onMouseEnter={e => this.props.onChargerMouseEnter(cluster.charger.pk, e)}
            onMouseLeave={e => this.props.onChargerMouseLeave(cluster.charger.pk, e)}
            router={router}
            intl={this.context.intl}
          />
      );
    });
    const markers = clusters.slice(0);
    if (currentLocation) {
      markers.push(
        <CurrentLocation
          key='currentLocation'
          lat={currentLocation.lat}
          lng={currentLocation.lng}
        />,
      );
    }
    return (
      <GoogleMap
        key={`home_map_${this.state.mapKey}`}
        bootstrapURLKeys={{ key: process.env.GOOGLE_MAPS_API_KEY }}
        center={center}
        zoom={zoom}
        onClick={this.props.onClick}
        onChange={this.handleMapChange}
        onGoogleApiLoaded={this.onMapLoaded}
        yesIWantToUseGoogleMapApiInternals
      >

        { markers }

      </GoogleMap>
    );
  }
}

export default ChargerMap;



// WEBPACK FOOTER //
// ./app/scripts/views/App/views/Home/components/ChargerMap/index.jsx